import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { api, type EventRow } from '../lib/api.js';

export const STATUS_LABEL: Record<string, string> = {
  DRAFT: '下書き',
  PUBLISHED: '公開中',
  PAUSED: '一時停止',
  ENDED: '終了',
};

/** イベント一覧。ここから作成・編集画面へ入る。 */
export function EventListPage(): JSX.Element {
  const navigate = useNavigate();
  const [items, setItems] = useState<EventRow[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);

  const load = async (): Promise<void> => {
    try {
      setItems((await api.listEvents()).items);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'イベントの取得に失敗しました');
    }
  };

  useEffect(() => {
    void load();
  }, []);

  const changeStatus = async (row: EventRow, action: 'publish' | 'pause' | 'end'): Promise<void> => {
    setError(null);
    setMessage(null);
    try {
      await api.setEventStatus(row.id, action);
      if (action === 'publish') {
        setMessage(`公開しました。連携した CocodePit ${row.cocodePitCount} 台がこのイベントの入口になります。`);
      }
      await load();
    } catch (e) {
      setError(e instanceof Error ? e.message : '状態の変更に失敗しました');
    }
  };

  return (
    <>
      <h1>イベント</h1>
      <p className="page-desc">イベントごとに連携する CocodePit（対象店舗）を登録して公開します。</p>

      <div className="actions" style={{ marginTop: 0, marginBottom: 16 }}>
        <button type="button" className="btn" onClick={() => navigate('/events/new')}>
          イベントを作成
        </button>
      </div>

      {error && <p className="error">{error}</p>}
      {message && <p className="ok">{message}</p>}

      <table>
        <thead>
          <tr>
            <th>イベント名</th>
            <th>抽選</th>
            <th>参加ルール</th>
            <th>開催期間</th>
            <th className="num">CocodePit</th>
            <th className="num">参加数</th>
            <th>状態</th>
            <th />
          </tr>
        </thead>
        <tbody>
          {items.map((e) => (
            <tr key={e.id}>
              <td>
                <Link to={`/events/${e.id}`}>{e.name}</Link>
              </td>
              <td>{e.lotteryType === 'SCRATCH' ? 'スクラッチ' : 'ルーレット'}</td>
              <td>
                {e.entryRule === 'ONCE_PER_DAY' ? '1日1回' : e.entryRule === 'ONCE_PER_PERIOD' ? '期間中1回' : '複数回可'}
              </td>
              <td>
                {new Date(e.startAt).toLocaleDateString('ja-JP')} 〜 {new Date(e.endAt).toLocaleDateString('ja-JP')}
              </td>
              <td className="num">{e.cocodePitCount}</td>
              <td className="num">{e.entryCount}</td>
              <td>
                <span className={`badge ${e.status === 'PUBLISHED' ? 'badge--published' : 'badge--draft'}`}>
                  {STATUS_LABEL[e.status] ?? e.status}
                </span>
              </td>
              <td>
                {(e.status === 'DRAFT' || e.status === 'PAUSED') && (
                  <button type="button" className="btn btn--ghost" onClick={() => void changeStatus(e, 'publish')}>
                    公開
                  </button>
                )}
                {e.status === 'PUBLISHED' && (
                  <button type="button" className="btn btn--ghost" onClick={() => void changeStatus(e, 'pause')}>
                    一時停止
                  </button>
                )}
                {e.status !== 'ENDED' && (
                  <button type="button" className="btn btn--ghost" onClick={() => void changeStatus(e, 'end')}>
                    終了
                  </button>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </>
  );
}
